import React, { FC, useContext, useEffect, useState } from "react";
import { Divider, FormControl, Stack, ToggleButton, ToggleButtonGroup, Toolbar, Typography } from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import SquareFootOutlinedIcon from "@mui/icons-material/SquareFootOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import { useHistory } from "react-router-dom";
import theme from "common/theme/theme";
import { menuItem } from "../menu/MenuDropdown";
import { OnOffToggle } from "../toolbar/OnOffToggle";
import ToolbarSection, { GetFormControlSxProps } from "../toolbar/ToolbarSection";
import useProjectStore, { DistanceUnit } from "common/store/ProjectStore";
import ToolbarDropdown from "./ToolbarDropdown";
import ToolbarIconButton, { StyledIconButton } from "./ToolbarIconButton";
import { SharedProjectContext } from "components/project/Project";
import { LanguageContext } from "common/contexts/LanguageContext";
import useSettingsStore from "common/store/SettingsStore";
import useNotificationStore from "common/store/NotificationStore";
import { useAuthContext } from "common/contexts/AuthContext";
import GuestUser from "common/components/dialogs/GuestUser";
import NotificationListDialog from "common/components/notification/NotificationListDialog";
import ScaleDialog, { ScaleStep } from "components/project/ScaleDialog";
import ToolbarExportButton from "./ToolbarExportButton";
import SharedProjectdDialog from "components/project/components/ShareProjectDialog";
import ConfirmationModal from "common/components/dialogs/ConfirmationModal";
import { useAuth0 } from "@auth0/auth0-react";

const createPercentMenus = (start: number, end: number, interval: number): menuItem[] => {
  const menuItems: menuItem[] = [];
  for (let i = start; i <= end; i += interval) {
    const key = `${i}`;
    const value = `${i} %`;
    menuItems.push({ key, value });
  }
  return menuItems;
};

const PlanningToolbar: FC = () => {
  const history = useHistory();
  const {
    zoomPercent,
    setZoomPercent,
    transparencyPercent,
    setTransparencyPercent,
    iconPercent,
    setIconPercent,
    enableStressTest,
    setEnableStressTest,
    distanceID,
    setDistanceID,
    saveProject,
    fetchProject,
    projectID,
    isOwned,
  } = useProjectStore();
  const { settings } = useSettingsStore();
  const { enqueueNotification } = useNotificationStore();
  const {
    scaleSet,
    setScaleSet,
    setProjectSaved,
    calculationsToggle,
    setCalculationsToggle,
    setIsBusyBackgroundActive,
    setNetworkCheck,
  } = useContext(SharedProjectContext);
  const { getString, selectedLanguageLocale } = useContext(LanguageContext);

  const { isAuthenticatedUser, getAccessToken } = useAuthContext();

  const [openGuestUserPopUp, setOpenGuestUserPopUp] = useState<boolean>(false);
  const [isOpenMessageList, setIsOpenMessageList] = useState<boolean>(false);
  const [isScaleDialogOpen, setIsScaleDialogOpen] = useState<boolean>(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState<boolean>(false);
  const [guestUserPopupTitle, setGuestUserPopupTitle] = useState<string>("");
  const [isSharedInfoOpen, setIsSharedInfoOpen] = useState<boolean>(false);
  const [sharedInfoTitle, setSharedInfoTitle] = useState<string>("");
  const [sharedInfoBody, setSharedInfoBody] = useState<string>("");

  useEffect(() => {
    setIsBusyBackgroundActive(isScaleDialogOpen);
  }, [isScaleDialogOpen, setIsBusyBackgroundActive]);

  const handleOnClickHome = () => {
    history.push(`/${selectedLanguageLocale}/`);
  };

  // Set function to handle setting scale
  const handleOnClickScale = () => {
    setIsScaleDialogOpen(true);
  };

  const handleOnClickSave = async () => {
    if (!isAuthenticatedUser) {
      setGuestUserPopupTitle(getString("msg_guest_user_save"));
      setOpenGuestUserPopUp(true);
      return;
    }

    if (!isOwned) {
      setSharedInfoTitle(getString("dlg_save_shared_title"));
      setSharedInfoBody(getString("dlg_save_shared_body"));
      setIsSharedInfoOpen(true);
      return;
    }

    let response = await saveProject(settings.apiBaseUrl, getAccessToken);
    if (response.succeeded) {
      enqueueNotification("save_project_success", getString("msg_proj_saved"), "success");
    } else {
      enqueueNotification("save_project_failure", response.errorMessage!, "error");
      return;
    }
    response = await fetchProject(settings.apiBaseUrl, getAccessToken, projectID!);
    if (!response.succeeded) {
      enqueueNotification("load_project_failure", getString("msg_proj_refetch_failed"), "error");
      return;
    }
    setProjectSaved(true);
  };

  const handleScaleDialogCancel = () => {
    setIsScaleDialogOpen(false);
  };

  const handleScaleDialogConfirm = () => {
    setIsScaleDialogOpen(false);
    setScaleSet(!scaleSet);
  };

  const handleSharedProject = () => {
    if (!isOwned) {
      setSharedInfoTitle(getString("dlg_share_shared_title"));
      setSharedInfoBody(getString("dlg_share_shared_body"));
      setIsSharedInfoOpen(true);
    } else if (!isAuthenticatedUser) {
      setGuestUserPopupTitle(getString("msg_guest_user_share"));
      setOpenGuestUserPopUp(true);
    } else {
      setIsShareDialogOpen(true);
    }
  };

  return (
    <Toolbar
      sx={{
        id: "toolbar",
        height: "70px",
        display: "flex",
        justifyContent: "flex-start",
        flexDirection: "row",
        overflow: "auto",
        flexWrap: "wrap",
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      }}
    >
      <ToolbarSection title={getString("bar_project")}>
        <ToolbarDropdown
          menuItems={createPercentMenus(100, 300, 20)}
          id="tb_zoom"
          label={getString("mnu_zoom")}
          dropdownWidth="60px"
          selectedValue={zoomPercent?.toString() ?? "100"}
          setSelectedValue={(str: string | null) => setZoomPercent(str === null ? 0 : parseInt(str))}
        />
        <ToolbarDropdown
          menuItems={createPercentMenus(0, 100, 20)}
          id="tb_transparent"
          label={getString("mnu_transparent")}
          dropdownWidth="60px"
          selectedValue={transparencyPercent?.toString() ?? "0"}
          setSelectedValue={(str: string | null) => setTransparencyPercent(str === null ? 0 : parseInt(str))}
        />
        <ToolbarDropdown
          menuItems={createPercentMenus(20, 300, 20)}
          id="tb_device_size"
          label={getString("mnu_dev_sz")}
          dropdownWidth="60px"
          selectedValue={iconPercent?.toString() ?? "0"}
          setSelectedValue={(str: string | null) => setIconPercent(str === null ? 0 : parseInt(str))}
        />
        <ToolbarIconButton
          label={getString("mnu_projects")}
          icon={<HomeOutlinedIcon sx={{ margin: "auto" }} />}
          onclick={handleOnClickHome}
        />
        <ToolbarIconButton
          label={getString("mnu_share_proj")}
          icon={<ReplyOutlinedIcon sx={{ margin: "auto", transform: "scaleX(-1)" }} />}
          onclick={handleSharedProject}
        />
        <ToolbarExportButton
          label={getString("mnu_export_proj")}
          icon={<IosShareOutlinedIcon sx={{ marginTop: "-8px" }} />}
        />
        <ToolbarIconButton
          label={getString("mnu_save_proj")}
          icon={<SaveOutlinedIcon sx={{ margin: "auto" }} />}
          onclick={handleOnClickSave}
        />
      </ToolbarSection>
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          bgcolor: theme.palette.primary.contrastText,
          opacity: 1,
          m: "8px 16px 4px 8px",
          width: "2px",
          border: "0",
        }}
      />
      <ToolbarSection title={getString("bar_settings")}>
        <ToolbarIconButton label={getString("mnu_plot")} icon={<InsertPhotoOutlinedIcon sx={{ margin: "auto" }} />} />
        <ToolbarIconButton
          label={getString("mnu_scale")}
          icon={<SquareFootOutlinedIcon sx={{ margin: "auto" }} />}
          onclick={handleOnClickScale}
        />
        <FormControl sx={GetFormControlSxProps()}>
          <Typography variant="toolbarlabel">{getString("mnu_units")}</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            sx={{
              margin: "auto",
              height: "18px",
              borderRadius: 0,
              backgroundColor: theme.palette.grey[300],
              "& .MuiToggleButton-root": {
                "&.Mui-selected": {
                  backgroundColor: theme.palette.common.white,
                  color: theme.palette.primary.main,
                },
                "&:hover": {
                  color: theme.palette.primary.light,
                },
              },
            }}
            value={distanceID}
            onChange={(event, newValue) => {
              setDistanceID(newValue);
            }}
          >
            <ToggleButton
              value={DistanceUnit.Feet}
              disabled={distanceID === DistanceUnit.Feet}
              sx={{ fontSize: "10px", borderRadius: 0 }}
            >
              {getString("meas_units_ft")}
            </ToggleButton>
            <ToggleButton
              value={DistanceUnit.Meters}
              disabled={distanceID === DistanceUnit.Meters}
              sx={{ fontSize: "10px", borderRadius: 0 }}
            >
              {getString("meas_units_mtr")}
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>
      </ToolbarSection>
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          bgcolor: theme.palette.primary.contrastText,
          opacity: 1,
          m: "8px 16px 4px 8px",
          width: "2px",
          border: "0",
        }}
      />
      <ToolbarSection title={getString("bar_network")}>
        <FormControl sx={GetFormControlSxProps()}>
          <Typography variant="toolbarlabel">{getString("mnu_analysis")}</Typography>
          <OnOffToggle
            sx={{ margin: "auto", mt: "2px" }}
            checked={calculationsToggle !== undefined ? calculationsToggle : true}
            onChange={(e) => setCalculationsToggle(e.target.checked)}
            inputProps={{ "aria-label": "controlled" }}
          />
        </FormControl>
        <ToolbarDropdown
          menuItems={createPercentMenus(25, 100, 25)}
          id="tb_network_accuracy"
          label={getString("mnu_accuracy")}
          dropdownWidth="60px"
          selectedValue={enableStressTest.toString() ?? "100"}
          setSelectedValue={(str: string | null) => {
            if (isAuthenticatedUser) {
              setEnableStressTest(str === null ? 0 : parseInt(str));
            }
          }}
          disabled={!isAuthenticatedUser}
        />
      </ToolbarSection>
      <Stack direction="row" sx={{ ml: "auto", mr: "-16px", height: "100%" }}>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            bgcolor: theme.palette.primary.contrastText,
            opacity: 1,
            m: "4px 4px",
            width: "2px",
            border: "0",
          }}
        />
        <StyledIconButton
          onClick={() => {
            setIsOpenMessageList(true);
            setNetworkCheck(true);
          }}
        >
          <WarningAmberOutlinedIcon />
        </StyledIconButton>
        <StyledIconButton onClick={() => window.open(settings.helpUrl, "_blank")}>
          <HelpOutlineOutlinedIcon />
        </StyledIconButton>
      </Stack>
      <GuestUser
        title={guestUserPopupTitle}
        isOpen={openGuestUserPopUp}
        setOpen={() => setOpenGuestUserPopUp(false)}
      ></GuestUser>

      <NotificationListDialog isOpen={isOpenMessageList} setOpen={setIsOpenMessageList} />
      <ScaleDialog
        stepNumber={ScaleStep.Explanation}
        isOpen={isScaleDialogOpen}
        handleClose={handleScaleDialogCancel}
        handleConfirm={handleScaleDialogConfirm}
      />
      {projectID && isShareDialogOpen && (
        <SharedProjectdDialog isOpen={isShareDialogOpen} setOpen={setIsShareDialogOpen} projectID={projectID} />
      )}

      <ConfirmationModal
        isOpen={isSharedInfoOpen}
        handleClose={() => setIsSharedInfoOpen(false)}
        handleConfirm={() => setIsSharedInfoOpen(false)}
        title={sharedInfoTitle}
        maxWidth="sm"
        hideDivider={true}
        hideCancel={true}
        confirmBtnLabel={getString("cmn_ok")}
      >
        <Typography variant="subtitle1" sx={{ pt: "24px" }}>
          {sharedInfoBody}
        </Typography>
      </ConfirmationModal>
    </Toolbar>
  );
};

export default PlanningToolbar;
